import { useState, type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { AGENCY_TZ, formatDate } from "../api";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "../components/Ui";
import Dialog from "../components/Dialog";
import {
  ActionForm,
  DataTable,
  RequestView,
  Tabs,
  label,
  useMutation,
  type InputField,
  type RecordData,
} from "../components/Workflow";
import { cleanTitle } from "../task-title";
import {
  formatCoreValues,
  formatMetric,
  goalLabel,
  listItems,
  parseCoreValues,
  parseLines,
  quarterChoices,
  quarterFilterChoices,
  quarterLabel,
  quarterOf,
  recentWeeks,
  weekLabel,
} from "../traction";

type Request<T> = { data: T | null; loading: boolean; error: string | null; reload: () => void };
type Rock = { id: number; title: string; rock_type: string; owner_name: string; quarter: string; due_date: string | null; status: string; completion_pct: number | null; why_it_matters: string | null };
type Entry = { week_of: string; actual_value: string | number | null; status: string | null; notes: string | null };
/** "Agency LER" in plain words, as Client health says it (the stored name is untouched). */
const metricName = (name: string) => name.replace(/\bAgency LER\b/g, "Revenue per dollar of team time");
type Metric = { id: number; metric_name: string; owner_name: string | null; goal_value: string | number; goal_direction: string; unit: string | null; weekly_entries: Entry[] | null; auto_fill?: boolean; data_source?: string | null };
/** The scorecard's health metrics (average score, clients at risk) are filled from client health scores. */
const isHealthMetric = (m: Metric) => String(m.data_source || "").startsWith("auto:health");
type ScoreRow = { signal_score: number; communication_score: number; client_status?: string | null };
type CoreValue = { value: string; description?: string };
type Vto = {
  core_values?: CoreValue[];
  core_focus_purpose?: string | null;
  core_focus_niche?: string | null;
  target_10_year?: string | null;
  target_3_year?: unknown;
  target_1_year?: unknown;
  marketing_target_market?: string | null;
  marketing_3_uniques?: string[] | null;
  marketing_proven_process?: string | null;
  marketing_proven_process_steps?: unknown;
  marketing_guarantee?: string | null;
  updated_at?: string | null;
  updated_by_name?: string | null;
};
type MeetingData = {
  meeting: RecordData;
  week_of: string;
  quarter: string;
  scorecard: { id: number; metric_name: string; goal_value: string | number; goal_direction: string; unit: string | null; actual_value: string | number | null; week_status: string | null }[];
  rocks: { id: number; title: string; owner: string; status: string; completion_pct: number | null }[];
  issues: { id: number; title: string; client_name: string | null; added_by_name: string | null }[];
  todos: { id: number; title: string; assignee_name: string | null; due_date: string | null }[];
};

/** Today on the agency's calendar, whoever is looking. */
const agencyToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: AGENCY_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const asList = <T,>(data: unknown): T[] => (Array.isArray(data) ? (data as T[]) : []);
const ROCK_STATUSES = ["on_track", "off_track", "complete", "not_started", "removed"];
const rockTone = (status: string) => (status === "off_track" ? "danger" : status === "complete" ? "success" : "neutral");

/** A plain table whose cells the caller builds; numbers line up on the right. */
function Table({ head, rows, className = "" }: {
  head: { label: string; num?: boolean }[];
  rows: { key: string | number; cells: ReactNode[] }[];
  className?: string;
}) {
  return (
    <div className="table-wrap">
      <table className={`workflow-table data-table ${className}`.trim()}>
        <thead><tr>{head.map((h) => <th key={h.label} className={h.num ? "num" : undefined}>{h.label}</th>)}</tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.key}>{row.cells.map((cell, i) => (
            <td key={head[i].label} data-label={head[i].label} className={[i === 0 ? "lead" : "", head[i].num ? "num" : ""].filter(Boolean).join(" ") || undefined}>{cell ?? "—"}</td>
          ))}</tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const Notice = ({ text }: { text: string }) => (text ? <p className="inline-notice" role="status">{text}</p> : null);

export default function TractionWorkspace() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const today = agencyToday();
  const current = quarterOf(today);
  const [view, setView] = useState("rocks");
  const [quarter, setQuarter] = useState(current);
  const [meetingId, setMeetingId] = useState("");
  const path = (
    {
      rocks: `/rocks?quarter=${quarter}`,
      scorecard: "/scorecard",
      issues: "/issues",
      vision: "/vto",
      meetings: "/l10",
      people: "/people-analyzer",
    } as Record<string, string>
  )[view];
  // For one render after a tab change the previous view's data is still held, so
  // each view checks the shape it takes (an object reaching a table crashed the page).
  const data = useApi<unknown>(path ? `/api/traction${path}` : null);
  return (
    <>
      <PageHeader
        title="Traction"
        eyebrow="Leadership"
        actions={<button className="button secondary" disabled={data.refreshing} onClick={data.reload}><RefreshCw className={data.refreshing ? "spin" : ""} aria-hidden="true" />{data.refreshing ? "Refreshing…" : "Refresh"}</button>}
      />
      {/* Phones: the six tabs stay on one line and scroll sideways. */}
      <div className="tr-tabstrip">
        <Tabs
          tabs={["rocks", "scorecard", "issues", "vision", "meetings", ...(isAdmin ? ["people"] : [])]}
          value={view}
          onChange={(v) => {
            setView(v);
            setMeetingId("");
          }}
        />
      </div>
      {view === "rocks" && <RocksView request={data} quarter={quarter} current={current} onQuarter={setQuarter} />}
      {view === "scorecard" && <ScorecardView request={data} today={today} isAdmin={isAdmin} />}
      {view === "issues" && <IssuesView request={data} />}
      {view === "vision" && <VisionView request={data} isAdmin={isAdmin} />}
      {view === "meetings" && <MeetingsView request={data} meetingId={meetingId} onOpen={setMeetingId} />}
      {view === "people" && <PeopleView request={data} />}
    </>
  );
}

/**
 * The quarter's rocks: one list, filtered by quarter (any quarter that holds rocks
 * can be picked, so a rock planned for next quarter never vanishes). A row opens
 * its progress update.
 */
function RocksView({ request, quarter, current, onQuarter }: {
  request: Request<unknown>;
  quarter: string;
  current: string;
  onQuarter: (quarter: string) => void;
}) {
  const { user } = useAuth();
  const rocks = asList<Rock>(request.data);
  const quarters = useApi<{ current: string; quarters: string[] }>("/api/traction/rocks/quarters");
  const people = useApi<{ id: number; full_name: string }[]>("/api/tasks/assignees");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Rock | null>(null);
  const [notice, setNotice] = useState("");
  const save = useMutation();
  const choices = quarterFilterChoices(current, quarters.data?.quarters || []);
  const quarterName = (q: string) => (q === current ? `${quarterLabel(q)} (this quarter)` : quarterLabel(q));
  const startAdding = () => { setNotice(""); save.setNotice(""); setAdding(true); };
  const newRock = <button className="button" onClick={startAdding}>New rock</button>;
  const owners = (people.data || []).map((p) => ({ value: String(p.id), label: p.full_name }));
  const defaultOwner = owners.some((o) => o.value === String(user?.id)) ? String(user?.id) : owners[0]?.value;
  const planning = quarterChoices(current);
  return (
    <>
      <Notice text={notice} />
      <section className="panel tr-rocks">
        <div className="toolbar">
          <label className="select-control">
            <span className="sr-only">Quarter</span>
            <select value={quarter} onChange={(event) => { setNotice(""); onQuarter(event.target.value); }}>
              {choices.map((q) => <option key={q} value={q}>{quarterName(q)}</option>)}
            </select>
          </label>
          <span className="toolbar-end">
            {rocks.length > 0 && <span className="result-count">{rocks.length} {rocks.length === 1 ? "rock" : "rocks"}</span>}
            {newRock}
          </span>
        </div>
        <RequestView request={request}>
          {rocks.length ? (
            <Table
              className="tr-table tr-rock-table"
              head={[{ label: "Rock" }, { label: "Owner" }, { label: "Type" }, { label: "Status" }, { label: "Progress", num: true }, { label: "Due" }]}
              rows={rocks.map((rock) => ({
                key: rock.id,
                cells: [
                  <>
                    <button type="button" className="tr-row-open" onClick={() => { setNotice(""); save.setNotice(""); setEditing(rock); }}>{rock.title}</button>
                    {rock.why_it_matters && <small className="tr-sub">{rock.why_it_matters}</small>}
                  </>,
                  rock.owner_name,
                  label(rock.rock_type),
                  <StatusBadge tone={rockTone(rock.status)}>{label(rock.status)}</StatusBadge>,
                  `${Number(rock.completion_pct || 0)}%`,
                  formatDate(rock.due_date),
                ],
              }))}
            />
          ) : (
            <EmptyState title={`No rocks for ${quarterLabel(quarter)}`} description="Rocks are the quarter's 3 to 7 most important goals, each with one owner." />
          )}
        </RequestView>
      </section>
      {adding && (
        <Dialog title="New rock" onClose={() => { if (!save.busy) setAdding(false); }}>
          {people.loading ? <LoadingState label="Loading the team" /> : people.error ? <ErrorState message={people.error} onRetry={people.reload} /> : (
            <ActionForm
              busy={save.busy}
              submit="Add rock"
              busyLabel="Adding…"
              onCancel={() => setAdding(false)}
              fields={[
                { name: "title", label: "Rock", required: true },
                { name: "owner_id", label: "Owner", required: true, options: owners, value: defaultOwner },
                { name: "quarter", label: "Quarter", options: planning.map((q) => ({ value: q, label: quarterName(q) })), value: planning.includes(quarter) ? quarter : current },
                { name: "rock_type", label: "Type", options: [{ value: "company", label: "Company" }, { value: "individual", label: "Individual" }], value: "company" },
                { name: "due_date", label: "Due date", type: "date", hint: "Leave empty for the last day of the quarter." },
                { name: "why_it_matters", label: "Why it matters", type: "textarea" },
              ]}
              onSubmit={async (fields) => {
                const result = await save.run<{ id: number; quarter?: string }>("/api/traction/rocks", { ...fields, owner_id: Number(fields.owner_id), due_date: fields.due_date || null });
                if (!result) return;
                const added = result.quarter || String(fields.quarter);
                setAdding(false);
                setNotice(`Rock added to ${quarterLabel(added)}.`);
                quarters.reload();
                // Show the quarter it went into, so it never seems to vanish.
                if (added !== quarter) onQuarter(added);
                else request.reload();
              }}
            />
          )}
          {save.feedback}
        </Dialog>
      )}
      {editing && (
        <Dialog title={editing.title} description={`${editing.owner_name}, due ${formatDate(editing.due_date)}`} onClose={() => { if (!save.busy) setEditing(null); }}>
          <ActionForm
            key={editing.id}
            busy={save.busy}
            onCancel={() => setEditing(null)}
            fields={[
              { name: "status", options: ROCK_STATUSES, value: editing.status, hint: "Off track adds the rock to Issues for the next meeting." },
              { name: "completion_pct", label: "Progress (%)", type: "number", min: 0, max: 100, step: "1", value: editing.completion_pct ?? 0 },
              { name: "notes", label: "Notes for this week", type: "textarea" },
            ]}
            onSubmit={async (fields) => {
              const result = await save.run(`/api/traction/rocks/${editing.id}/status`, fields, "PUT");
              if (!result) return;
              setEditing(null);
              setNotice("Progress saved.");
              request.reload();
            }}
          />
          {save.feedback}
        </Dialog>
      )}
    </>
  );
}

/**
 * The scorecard: each metric's goal and its last six weeks, green or red against
 * the goal. A week's cell opens its entry; this week's empty cell reads "Add".
 */
function ScorecardView({ request, today, isAdmin }: { request: Request<unknown>; today: string; isAdmin: boolean }) {
  const metrics = asList<Metric>(request.data);
  const weeks = recentWeeks(today, 6);
  // The health metrics are only a reading when most scores rest on enough signals
  // (Client health's rule: Signal and Communication at 70 or more). Until then their
  // numbers are placeholders: drawn grey, never green, with the reason under the name.
  const healthScores = useApi<{ scores: ScoreRow[] }>(metrics.some(isHealthMetric) ? "/api/health/scores" : null);
  const scored = (healthScores.data?.scores || []).filter((sc) => !sc.client_status || sc.client_status === "active");
  const partial = scored.filter((sc) => sc.signal_score < 70 || sc.communication_score < 70).length;
  const healthPartial = scored.length > 0 && partial * 2 > scored.length;
  const thisWeek = weeks[weeks.length - 1];
  const fill = useMutation();
  const save = useMutation();
  const [notice, setNotice] = useState("");
  const [entry, setEntry] = useState<{ metric: Metric; week: string; existing?: Entry } | null>(null);
  async function fillWeek() {
    setNotice("");
    const result = await fill.run<{ week_of?: string; filled?: string[]; kept?: string[] }>("/api/traction/scorecard/auto-populate", {});
    if (!result) return;
    request.reload();
    const filled = result.filled || [];
    const week = weekLabel(result.week_of || thisWeek);
    const numbers = (n: number) => `${n} ${n === 1 ? "number" : "numbers"}`;
    const kept = result.kept?.length ? ` Kept ${numbers(result.kept.length)} typed by hand.` : "";
    setNotice(filled.length ? `Filled ${numbers(filled.length)} for the week of ${week}: ${filled.join(", ")}.${kept}` : `Nothing to fill for the week of ${week} yet.${kept}`);
  }
  const existingNumber = entry?.existing?.actual_value;
  return (
    <>
      <Notice text={notice} />
      {fill.error && <p className="inline-notice danger" role="alert">{fill.error}</p>}
      <section className="panel tr-scorecard-panel">
        {/* A titled header like Issues and People, with Fill this week beside the title. */}
        <div className="panel-heading">
          <div><h2>Weekly scorecard</h2></div>
          {isAdmin && <button className="button secondary" onClick={() => void fillWeek()} disabled={fill.busy}>{fill.busy ? "Filling…" : "Fill this week"}</button>}
        </div>
        <RequestView request={request}>
          {metrics.length ? (
            <Table
              className="tr-scorecard"
              head={[{ label: "Metric" }, { label: "Owner" }, { label: "Goal", num: true }, ...weeks.map((week) => ({ label: weekLabel(week), num: true }))]}
              rows={metrics.map((metric) => {
                const byWeek = new Map((metric.weekly_entries || []).map((e) => [String(e.week_of).slice(0, 10), e]));
                const placeholder = healthPartial && isHealthMetric(metric);
                return {
                  key: metric.id,
                  cells: [
                    placeholder ? <>{metricName(metric.metric_name)}<small className="tr-sub">{`Not enough data yet (${partial} of ${scored.length} partial)`}</small></> : metricName(metric.metric_name),
                    metric.owner_name || "—",
                    goalLabel(metric.goal_value, metric.goal_direction, metric.unit),
                    ...weeks.map((week) => {
                      const e = byWeek.get(week);
                      const has = e != null && e.actual_value != null;
                      const shown = has ? formatMetric(e.actual_value, metric.unit) : week === thisWeek ? "Add" : "—";
                      const state = placeholder ? ", not enough data yet" : has ? (e.status === "red" ? ", off goal" : e.status === "green" ? ", on goal" : "") : ", not entered";
                      return (
                        <button
                          type="button"
                          className={`tr-cell${has && e.status && !placeholder ? ` is-${e.status}` : ""}${placeholder && has ? " is-placeholder" : ""}${!has && week === thisWeek ? " is-add" : ""}`}
                          title={e?.notes || undefined}
                          aria-label={`${metric.metric_name}, week of ${weekLabel(week)}: ${has ? shown : "nothing"}${state}`}
                          onClick={() => { save.setNotice(""); setEntry({ metric, week, existing: e }); }}
                        >
                          {shown}
                          {placeholder && has && <span className="score-partial">Partial</span>}
                        </button>
                      );
                    }),
                  ],
                };
              })}
            />
          ) : (
            <EmptyState title="No scorecard metrics yet" description="The scorecard is a few numbers checked every week against a goal." />
          )}
        </RequestView>
      </section>
      {entry && (
        <Dialog
          title={entry.metric.metric_name}
          description={`Week of ${weekLabel(entry.week)}. Goal ${goalLabel(entry.metric.goal_value, entry.metric.goal_direction, entry.metric.unit)}.${entry.metric.auto_fill ? " The app fills this one; a number typed here is kept." : ""}`}
          onClose={() => { if (!save.busy) setEntry(null); }}
        >
          <ActionForm
            key={`${entry.metric.id}-${entry.week}`}
            busy={save.busy}
            onCancel={() => setEntry(null)}
            fields={[
              { name: "actual_value", label: "Number", type: "number", step: "any", required: true, value: existingNumber == null ? "" : Number(existingNumber) },
              { name: "notes", type: "textarea", value: entry.existing?.notes || "" },
            ]}
            onSubmit={async (fields) => {
              const result = await save.run("/api/traction/scorecard/entry", { ...fields, metric_id: entry.metric.id, week_of: entry.week });
              if (!result) return;
              setEntry(null);
              setNotice(`${entry.metric.metric_name} saved for the week of ${weekLabel(entry.week)}.`);
              request.reload();
            }}
          />
          {save.feedback}
        </Dialog>
      )}
    </>
  );
}

function IssuesView({ request }: { request: Request<unknown> }) {
  const rows = asList<RecordData>(request.data).map((row) => ({ ...row, client: row.client_name, added_by: row.added_by_name }));
  const [editing, setEditing] = useState<RecordData | null>(null);
  const [notice, setNotice] = useState("");
  const save = useMutation();
  const open = (row: RecordData) => { setNotice(""); save.setNotice(""); setEditing(row); };
  return (
    <>
      <Notice text={notice} />
      <section className="panel">
        <div className="panel-heading">
          <div><h2>Open issues</h2></div>
          <button className="button" onClick={() => open({ new: true })}>Add issue</button>
        </div>
        <RequestView request={request}>
          {/* Every tab's empty state is the same block (Rocks and Scorecard use it too). */}
          {rows.length ? (
            <DataTable
              rows={rows}
              columns={["title", "priority", "status", "client", "added_by"]}
              action={(row) => <button className="button secondary" onClick={() => open(row)}>Discuss / solve</button>}
            />
          ) : <EmptyState title="No open issues" description="Add one to discuss it at the next Level 10 meeting." />}
        </RequestView>
      </section>
      {editing && (
        <Dialog title={editing.new ? "New issue" : String(editing.title || "Issue")} onClose={() => { if (!save.busy) setEditing(null); }}>
          <ActionForm
            key={String(editing.id || "new")}
            busy={save.busy}
            onCancel={() => setEditing(null)}
            fields={
              (editing.new
                ? [
                    { name: "title", label: "Issue", required: true },
                    { name: "priority", type: "number", min: 1, value: 50, hint: "Lower numbers are discussed first." },
                  ]
                : [
                    { name: "status", options: ["open", "in_ids", "solved"], value: editing.status },
                    { name: "ids_discussion", label: "Discussion", type: "textarea", value: editing.ids_discussion },
                    { name: "solution", type: "textarea", value: editing.solution },
                  ]) as InputField[]
            }
            onSubmit={async (fields) => {
              const result = await save.run<RecordData>(`/api/traction/issues${editing.new ? "" : `/${editing.id}`}`, fields, editing.new ? "POST" : "PUT");
              if (!result) return;
              setEditing(null);
              setNotice(String(result.note || (editing.new ? "Issue added." : "Issue saved.")));
              request.reload();
            }}
          />
          <p className="workflow-note">A solution that changes budgets or campaigns goes to Approvals before anything changes.</p>
          {save.feedback}
        </Dialog>
      )}
    </>
  );
}

const Missing = () => <span className="muted">Not written yet</span>;

function VisionSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="tr-vision-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Lines({ items, ordered = false }: { items: string[]; ordered?: boolean }) {
  if (!items.length) return <Missing />;
  const List = ordered ? "ol" : "ul";
  return <List className="tr-lines">{items.map((item, i) => <li key={i}>{item}</li>)}</List>;
}

/**
 * The V/TO as a document: core values, core focus, the 10-year target, the
 * marketing strategy, the 3-year picture and the 1-year plan.
 */
function VisionView({ request, isAdmin }: { request: Request<unknown>; isAdmin: boolean }) {
  const vto = (request.data && !Array.isArray(request.data) ? request.data : {}) as Vto;
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState("");
  const save = useMutation();
  const values = (Array.isArray(vto.core_values) ? vto.core_values : []).filter((v) => v && typeof v.value === "string" && v.value.trim());
  const uniques = listItems(vto.marketing_3_uniques);
  const steps = listItems(vto.marketing_proven_process_steps);
  const threeYear = listItems(vto.target_3_year);
  const oneYear = listItems(vto.target_1_year);
  return (
    <RequestView request={request}>
      <Notice text={notice} />
      <section className="panel tr-vision">
        <div className="tr-vision-head">
          <p className="tr-updated">{vto.updated_at ? `Last updated ${formatDate(vto.updated_at)}${vto.updated_by_name ? ` by ${vto.updated_by_name}` : ""}` : "Not written yet"}</p>
          {isAdmin && <button className="button secondary" onClick={() => { setNotice(""); save.setNotice(""); setEditing(true); }}>Edit vision</button>}
        </div>
        <VisionSection title="Core values">
          {values.length ? (
            <dl className="tr-values">{values.map((v) => <div key={v.value}><dt>{v.value}</dt>{v.description && <dd>{v.description}</dd>}</div>)}</dl>
          ) : <Missing />}
        </VisionSection>
        <VisionSection title="Core focus">
          <dl className="tr-pairs">
            <div><dt>Purpose</dt><dd>{vto.core_focus_purpose || <Missing />}</dd></div>
            <div><dt>Niche</dt><dd>{vto.core_focus_niche || <Missing />}</dd></div>
          </dl>
        </VisionSection>
        <VisionSection title="10-year target"><p className="tr-text">{vto.target_10_year || <Missing />}</p></VisionSection>
        <VisionSection title="Marketing strategy">
          <dl className="tr-pairs">
            <div><dt>Target market</dt><dd>{vto.marketing_target_market || <Missing />}</dd></div>
            <div><dt>Three uniques</dt><dd><Lines items={uniques} /></dd></div>
            <div><dt>Proven process</dt><dd>{vto.marketing_proven_process || (!steps.length && <Missing />)}{steps.length > 0 && <Lines items={steps} ordered />}</dd></div>
            <div><dt>Guarantee</dt><dd>{vto.marketing_guarantee || <Missing />}</dd></div>
          </dl>
        </VisionSection>
        <VisionSection title="3-year picture"><Lines items={threeYear} /></VisionSection>
        <VisionSection title="1-year plan"><Lines items={oneYear} /></VisionSection>
      </section>
      {editing && (
        <Dialog title="Edit vision" description="Lists take one item per line." onClose={() => { if (!save.busy) setEditing(false); }}>
          <ActionForm
            busy={save.busy}
            onCancel={() => setEditing(false)}
            fields={[
              { name: "core_values", label: "Core values", type: "textarea", value: formatCoreValues(vto.core_values), hint: "One per line, as Value: what it means." },
              { name: "core_focus_purpose", label: "Purpose", type: "textarea", value: vto.core_focus_purpose },
              { name: "core_focus_niche", label: "Niche", type: "textarea", value: vto.core_focus_niche },
              { name: "target_10_year", label: "10-year target", type: "textarea", value: vto.target_10_year },
              { name: "marketing_target_market", label: "Target market", type: "textarea", value: vto.marketing_target_market },
              { name: "marketing_3_uniques", label: "Three uniques", type: "textarea", value: uniques.join("\n") },
              { name: "marketing_proven_process", label: "Proven process", type: "textarea", value: vto.marketing_proven_process },
              { name: "marketing_guarantee", label: "Guarantee", type: "textarea", value: vto.marketing_guarantee },
              { name: "target_3_year", label: "3-year picture", type: "textarea", value: threeYear.join("\n") },
              { name: "target_1_year", label: "1-year plan", type: "textarea", value: oneYear.join("\n") },
            ]}
            onSubmit={async (fields) => {
              const text = (name: string) => String(fields[name] ?? "");
              const result = await save.run("/api/traction/vto", {
                core_values: parseCoreValues(text("core_values"), vto.core_values),
                core_focus_purpose: text("core_focus_purpose"),
                core_focus_niche: text("core_focus_niche"),
                target_10_year: text("target_10_year"),
                marketing_target_market: text("marketing_target_market"),
                marketing_3_uniques: parseLines(text("marketing_3_uniques")),
                marketing_proven_process: text("marketing_proven_process"),
                marketing_guarantee: text("marketing_guarantee"),
                target_3_year: parseLines(text("target_3_year")),
                target_1_year: parseLines(text("target_1_year")),
              }, "PUT");
              if (!result) return;
              setEditing(false);
              setNotice("Vision saved.");
              request.reload();
            }}
          />
          {save.feedback}
        </Dialog>
      )}
    </RequestView>
  );
}

// The Level 10 agenda, in order, with its usual minutes.
const AGENDA: [string, string, number][] = [
  ["segue", "Segue", 5],
  ["scorecard", "Scorecard", 5],
  ["rock_review", "Rock review", 5],
  ["headlines", "Customer and employee headlines", 5],
  ["todo_review", "To-do list", 5],
  ["ids", "IDS: identify, discuss, solve", 60],
  ["conclude", "Conclude", 5],
];

function MeetingsView({ request, meetingId, onOpen }: { request: Request<unknown>; meetingId: string; onOpen: (id: string) => void }) {
  const meetings = asList<RecordData>(request.data);
  const meeting = useApi<MeetingData>(meetingId ? `/api/traction/l10/${meetingId}/data` : null);
  const save = useMutation();
  const step = useMutation();
  const [dialog, setDialog] = useState<"schedule" | "complete" | null>(null);
  const [notice, setNotice] = useState("");
  const m = meeting.data;
  const openDialog = (kind: "schedule" | "complete") => { setNotice(""); save.setNotice(""); setDialog(kind); };
  return (
    <div className="workflow-stack">
      <Notice text={notice} />
      <section className="panel workflow-panel">
        <div className="tr-panel-head">
          <h2>Level 10 meetings</h2>
          <button className="button" onClick={() => openDialog("schedule")}>Schedule meeting</button>
        </div>
        <RequestView request={request}>
          {meetings.length ? (
            <DataTable
              rows={meetings}
              columns={["meeting_date", "status", "rating"]}
              action={(row) => <button className="button secondary" onClick={() => { setNotice(""); onOpen(String(row.id)); }}>Open meeting</button>}
            />
          ) : <EmptyState title="No Level 10 meetings yet" description="Schedule one to start the weekly rhythm." />}
        </RequestView>
      </section>
      {meetingId && (
        <RequestView request={meeting}>
          {m && (
            <section className="panel workflow-panel tr-meeting">
              <div className="tr-panel-head">
                <h2>Meeting on {formatDate(m.meeting.meeting_date)}</h2>
                {m.meeting.status === "complete"
                  ? <span className="tr-updated">Rated {m.meeting.rating} out of 10</span>
                  : <button className="button" onClick={() => openDialog("complete")}>Complete meeting</button>}
              </div>
              {step.error && <p className="inline-notice danger" role="alert">{step.error}</p>}
              <ol className="tr-agenda">
                {AGENDA.map(([key, name, minutes]) => (
                  <li key={key}>
                    <label className="meeting-step">
                      <input
                        type="checkbox"
                        disabled={step.busy}
                        checked={Boolean(m.meeting[`${key}_complete`])}
                        onChange={async (event) => {
                          const result = await step.run(`/api/traction/l10/${meetingId}/section`, { section: key, complete: event.target.checked }, "PUT");
                          if (result) meeting.reload();
                        }}
                      />
                      <span>{name}</span>
                      <small>{minutes} min</small>
                    </label>
                    {key === "scorecard" && (m.scorecard.length ? (
                      <Table
                        className="tr-mini"
                        head={[{ label: "Metric" }, { label: "Goal", num: true }, { label: m.week_of ? `Week of ${weekLabel(m.week_of)}` : "This week", num: true }]}
                        rows={m.scorecard.map((s) => ({
                          key: s.id,
                          cells: [s.metric_name, goalLabel(s.goal_value, s.goal_direction, s.unit), <span className={`tr-value${s.week_status ? ` is-${s.week_status}` : ""}`}>{formatMetric(s.actual_value, s.unit)}</span>],
                        }))}
                      />
                    ) : <p className="workflow-note">No scorecard metrics yet.</p>)}
                    {key === "rock_review" && (m.rocks.length ? (
                      <Table
                        className="tr-mini"
                        head={[{ label: "Rock" }, { label: "Owner" }, { label: "Status" }, { label: "Progress", num: true }]}
                        rows={m.rocks.map((r) => ({ key: r.id, cells: [r.title, r.owner, <StatusBadge tone={rockTone(r.status)}>{label(r.status)}</StatusBadge>, `${Number(r.completion_pct || 0)}%`] }))}
                      />
                    ) : <p className="workflow-note">No rocks for {m.quarter ? quarterLabel(m.quarter) : "this quarter"}.</p>)}
                    {key === "todo_review" && (m.todos.length ? (
                      <Table
                        className="tr-mini"
                        head={[{ label: "To-do" }, { label: "Who" }, { label: "Due" }]}
                        rows={m.todos.map((t) => ({ key: t.id, cells: [cleanTitle(t.title), t.assignee_name || "Unassigned", formatDate(t.due_date)] }))}
                      />
                    ) : <p className="workflow-note">No to-dos from this meeting.</p>)}
                    {key === "ids" && (m.issues.length ? (
                      <Table
                        className="tr-mini"
                        head={[{ label: "Issue" }, { label: "Client" }, { label: "Added by" }]}
                        rows={m.issues.map((i) => ({ key: i.id, cells: [i.title, i.client_name || "Agency", i.added_by_name || "—"] }))}
                      />
                    ) : <p className="workflow-note">No open issues.</p>)}
                  </li>
                ))}
              </ol>
              {m.meeting.cascading_messages && (
                <div className="tr-vision-section">
                  <h2>Cascading messages</h2>
                  <p className="tr-text">{String(m.meeting.cascading_messages)}</p>
                </div>
              )}
            </section>
          )}
        </RequestView>
      )}
      {dialog && (
        <Dialog title={dialog === "schedule" ? "Schedule meeting" : "Complete meeting"} onClose={() => { if (!save.busy) setDialog(null); }}>
          <ActionForm
            key={dialog}
            busy={save.busy}
            submit={dialog === "schedule" ? "Schedule" : "Complete meeting"}
            onCancel={() => setDialog(null)}
            fields={
              dialog === "schedule"
                ? [{ name: "meeting_date", label: "Meeting date", type: "date", required: true, value: agencyToday() }]
                : [
                    { name: "rating", label: "Rating (1 to 10)", type: "number", min: 1, max: 10, step: "1", required: true },
                    { name: "cascading_messages", label: "Cascading messages", type: "textarea" },
                  ]
            }
            onSubmit={async (fields) => {
              const result = await save.run<{ id?: number }>(dialog === "schedule" ? "/api/traction/l10" : `/api/traction/l10/${meetingId}/complete`, fields, dialog === "schedule" ? "POST" : "PUT");
              if (!result) return;
              setDialog(null);
              request.reload();
              if (dialog === "schedule") {
                setNotice("Meeting scheduled.");
                if (result.id) onOpen(String(result.id));
              } else {
                setNotice("Meeting completed.");
                meeting.reload();
              }
            }}
          />
          {save.feedback}
        </Dialog>
      )}
    </div>
  );
}

function PeopleView({ request }: { request: Request<unknown> }) {
  const rows = asList<RecordData>(request.data);
  const people = useApi<RecordData[]>("/api/tasks/assignees");
  const [editing, setEditing] = useState<RecordData | null>(null);
  const [notice, setNotice] = useState("");
  const save = useMutation();
  const open = (row: RecordData) => { setNotice(""); save.setNotice(""); setEditing(row); };
  return (
    <>
      <Notice text={notice} />
      <section className="panel">
        <div className="panel-heading">
          <div><h2>People reviews</h2></div>
          <button className="button" onClick={() => open({ new: true })}>Record review</button>
        </div>
        <RequestView request={request}>
          {rows.length ? (
            <DataTable
              rows={rows}
              columns={["full_name", "quarter", "gets_it", "wants_it", "capacity", "notes"]}
              action={(row) => <button className="button secondary" onClick={() => open(row)}>Edit review</button>}
            />
          ) : <EmptyState title="No people reviews yet" description="Reviews you record show up here." />}
        </RequestView>
      </section>
      {editing && (
        <Dialog title="People review" onClose={() => { if (!save.busy) setEditing(null); }}>
          <ActionForm
            key={String(editing.user_id || "new")}
            busy={save.busy}
            onCancel={() => setEditing(null)}
            fields={[
              {
                name: "user_id",
                label: "Team member",
                required: true,
                value: editing.user_id,
                options: (people.data || []).map((p) => ({ value: String(p.id), label: String(p.full_name) })),
              },
              ...["gets_it", "wants_it", "capacity"].map((name) => ({ name, options: ["+", "+/-", "-"], value: editing[name] || "+/-" })),
              { name: "notes", type: "textarea", value: editing.notes },
            ]}
            onSubmit={async (fields) => {
              const result = await save.run(`/api/traction/people-analyzer/${fields.user_id}`, { ...fields, core_values_ratings: editing.core_values_ratings || {} }, "PUT");
              if (!result) return;
              setEditing(null);
              setNotice("Review saved.");
              request.reload();
            }}
          />
          {save.feedback}
        </Dialog>
      )}
    </>
  );
}
